import { useState } from "react";
import { Users, Heart, Clock, MapPin, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOLUNTEER_IMAGE = "https://images.unsplash.com/photo-1560220604-1985ebfe28b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY29tbXVuaXR5JTIwdm9sdW50ZWVycyUyMGdyb3VwfGVufDB8fHx8MTc3MDAwODU4OHww&ixlib=rb-4.1.0&q=85";

const interestOptions = [
  "Community Outreach",
  "Event Planning",
  "Youth Mentorship",
  "Administrative Support",
  "Fundraising",
  "Social Media & Marketing",
  "Peace Education",
  "Public Speaking"
];

const availabilityOptions = [
  "Weekday Mornings",
  "Weekday Afternoons",
  "Weekday Evenings",
  "Weekend Mornings",
  "Weekend Afternoons",
  "Weekend Evenings"
];

const benefits = [
  { icon: Heart, title: "Make a Difference", description: "Directly impact lives in your community" },
  { icon: Users, title: "Build Connections", description: "Meet like-minded individuals passionate about peace" },
  { icon: Clock, title: "Flexible Hours", description: "Volunteer on your own schedule" }
];

export default function Volunteer() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    interests: [],
    availability: [],
    experience: "",
    why_volunteer: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/volunteers`, formData);
      setSubmitted(true);
      toast.success("Thank you for signing up to volunteer!");
    } catch (error) {
      console.error("Error submitting volunteer form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="volunteer-success" className="min-h-[70vh] flex items-center justify-center py-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
            Thank You for Signing Up!
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            We're excited to have you join our team of volunteers. Our coordinator will contact you soon with more information about upcoming opportunities.
          </p>
          <Button 
            onClick={() => setSubmitted(false)} 
            variant="outline"
            className="btn-secondary"
          >
            Submit Another Application
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="volunteer-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Join Our Team
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] leading-tight mb-6">
                Become a Volunteer
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Join our community of dedicated volunteers making a real difference. Whether you have a few hours a week or can commit to regular involvement, there's a place for you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-white p-4 rounded-xl shadow-sm">
                    <benefit.icon className="w-8 h-8 text-[#0056D2] mb-2" />
                    <h3 className="font-semibold text-[#0F172A] text-sm">{benefit.title}</h3>
                    <p className="text-xs text-slate-500">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src={VOLUNTEER_IMAGE}
                alt="Diverse group of volunteers"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-2">Volunteer Application</h2>
            <p className="text-slate-600 mb-8">Fill out the form below to join our volunteer team.</p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0056D2]" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="volunteer-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="volunteer-last-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="volunteer-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="volunteer-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#0056D2]" />
                  Address (Optional)
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-1 h-12 rounded-lg"
                      data-testid="volunteer-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="mt-1 h-12 rounded-lg"
                        data-testid="volunteer-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="mt-1 h-12 rounded-lg"
                        data-testid="volunteer-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        className="mt-1 h-12 rounded-lg"
                        data-testid="volunteer-zip"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Interests */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#0056D2]" />
                  Areas of Interest
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {interestOptions.map((interest) => (
                    <div key={interest} className="flex items-center space-x-2">
                      <Checkbox
                        id={`interest-${interest}`}
                        checked={formData.interests.includes(interest)}
                        onCheckedChange={(checked) => handleCheckboxChange("interests", interest, checked)}
                        data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <Label htmlFor={`interest-${interest}`} className="text-sm text-slate-600 cursor-pointer">
                        {interest}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0056D2]" />
                  Availability
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availabilityOptions.map((time) => (
                    <div key={time} className="flex items-center space-x-2">
                      <Checkbox
                        id={`availability-${time}`}
                        checked={formData.availability.includes(time)}
                        onCheckedChange={(checked) => handleCheckboxChange("availability", time, checked)}
                        data-testid={`availability-${time.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <Label htmlFor={`availability-${time}`} className="text-sm text-slate-600 cursor-pointer">
                        {time}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Experience & Motivation */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="experience">Previous Volunteer Experience</Label>
                  <Textarea
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="Tell us about any relevant experience..."
                    className="mt-1 rounded-lg resize-none"
                    rows={3}
                    data-testid="volunteer-experience"
                  />
                </div>
                <div>
                  <Label htmlFor="why_volunteer">Why do you want to volunteer with us?</Label>
                  <Textarea
                    id="why_volunteer"
                    name="why_volunteer"
                    value={formData.why_volunteer}
                    onChange={handleInputChange}
                    placeholder="Share your motivation..."
                    className="mt-1 rounded-lg resize-none"
                    rows={3}
                    data-testid="volunteer-why"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-14 text-lg"
                data-testid="volunteer-submit-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
